import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { Tooltip } from "antd";
import { getCurrentTimestamp } from "../../../../Helper/DatePicker/DateConverter";
import CustomIcon from "../../../../Components/CustomIcon";
import copy from "copy-to-clipboard";
import { useToast } from "../../../../Hooks/useToast";
import { getSohColor, formatDaysAge, getEmptyCellText } from "../SOHBreakdown/SOHBreakdownTableConfig";
import { formatComputedDate } from "../utils/tableUtils";

// ─── Relative-time helper ─────────────────────────────────────────────────────
export function timeAgo(dateStr) {
  if (!dateStr) return "—";
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (days <= 0)  return "Today";
  if (days === 1) return "1 day ago";
  if (days < 7)   return `${days} days ago`;
  if (days < 14)  return "1 week ago";
  if (days < 30)  return `${Math.floor(days / 7)} weeks ago`;
  if (days < 60)  return "1 month ago";
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  if (days < 730) return "1 year ago";
  return `${Math.floor(days / 365)} years ago`;
}

// ─── Device ID / Battery ID cell ──────────────────────────────────────────────
const DeviceIDCell = ({ text, isActive, onNavigate }) => {
  const { openNotification } = useToast();

  const handleCopy = (e) => {
    e.stopPropagation();
    copy(text);
    openNotification({ text: "Copied to Clipboard" });
  };

  return (
    <Box display="flex" alignItems="center" gap={0.75}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          flexShrink: 0,
          backgroundColor: isActive ? "#2DB55D" : "#9E9E9E",
        }}
      />
      <Typography
        variant="fw400s14lh24c0061CA"
        onClick={onNavigate}
        sx={{
          cursor: "pointer",
          "&:hover": { color: "#4D44D8 !important" },
        }}
      >
        {text}
      </Typography>
      <Tooltip title="Click to copy" placement="top">
        <IconButton
          disableRipple
          disableTouchRipple
          disableFocusRipple
          onClick={handleCopy}
        >
          <CustomIcon name="copy-icon" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// ─── Custom renderers factory ─────────────────────────────────────────────────
export const getSohCustomRenderers = (onBatteryIDClick) => ({
  "days-to-duration": (record, column) => {
    const days = record?.[column.dataIndex];
    return (
      <Typography sx={{ fontSize: 13, color: "#202124" }}>
        {days == null || isNaN(days) ? getEmptyCellText(column, formatDaysAge(days)) : formatDaysAge(days)}
      </Typography>
    );
  },

  // deviceID / licensePlateNo column: status dot + link + copy (field driven by dataIndex)
  "digital-twin-link": (record, config) => (
    <DeviceIDCell
      text={config?.dataIndex ? (record[config.dataIndex] ?? record.deviceID) : record.deviceID}
      isActive={record.pingStatus?.toLowerCase() === "active"}
      onNavigate={() => onBatteryIDClick(record)}
    />
  ),
  // batteryID column: status dot + battery ID link + copy
  "battery-id-link": (record) => (
    <DeviceIDCell
      text={record.batteryID}
      isActive={record.pingStatus?.toLowerCase() === "active"}
      onNavigate={() => onBatteryIDClick(record)}
    />
  ),
  "time-ago": (record) => (
    <Typography variant="fw400s14lh24c3D4B5A">{timeAgo(record.addedOn)}</Typography>
  ),
  // Field-driven date cell (DD MMM YYYY) — e.g. sohComputedDate / stringNSohComputedDate
  "date": (record, config) => (
    <Typography sx={{ fontSize: 13, color: "#202124" }}>
      {formatComputedDate(config?.dataIndex != null ? record?.[config.dataIndex] : null)}
    </Typography>
  ),
  "soh-progress-bar": (record, config) => {
    // Field-driven: read the column's own dataIndex (falls back to sohEst for the
    // legacy SOH column). Lets the same bar render minSOHBMS / string1SohEst…string4SohEst.
    const raw = config?.dataIndex != null ? record?.[config.dataIndex] : record?.sohEst;
    const soh = parseFloat(raw);
    if (raw == null || isNaN(soh)) return <Typography sx={{ fontSize: 13, color: "#9E9E9E" }}>{getEmptyCellText(config, "—")}</Typography>;
    const decimalPlaces = config?.decimalPlaces ?? 1;
    const color = getSohColor(soh);
    const width = Math.min(100, Math.max(0, soh));
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ width: 56, height: 5, bgcolor: "#E0E0E0", borderRadius: "3px", flexShrink: 0 }}>
          <Box sx={{ width: `${width}%`, height: "100%", bgcolor: color, borderRadius: "3px" }} />
        </Box>
        <Typography sx={{ fontSize: 13, color: "#202124" }}>{soh.toFixed(decimalPlaces)}%</Typography>
      </Box>
    );
  },
});

export const getSohCSVFileName = () =>
  `SOH_BatteryList_${getCurrentTimestamp("YYYY-MM-DD")}.csv`;
